import React, { useEffect, useState } from "react";
import { adminApi } from "../api";
import PasswordInput from "./PasswordInput";
import AdminSelect from "./AdminSelect";
import "./EmailProviderPanel.css";

const PROVIDERS = [
  {
    id: "sendgrid",
    label: "SendGrid",
    blurb: "HTTPS API. Best deliverability + analytics, requires verified sender or authenticated domain.",
  },
  {
    id: "smtp",
    label: "Generic SMTP",
    blurb: "Any SMTP server — Mailgun, Postmark, Office 365, Gmail app password, or a self-hosted relay.",
  },
  {
    id: "ses",
    label: "Amazon SES",
    blurb: "Uses SES's SMTP endpoints and dedicated SMTP credentials (no AWS SDK required).",
  },
];

const SES_REGIONS = [
  "us-east-1", "us-east-2", "us-west-1", "us-west-2",
  "eu-west-1", "eu-west-2", "eu-central-1", "eu-north-1",
  "ap-south-1", "ap-southeast-1", "ap-southeast-2", "ap-northeast-1",
  "ca-central-1", "sa-east-1",
];

export default function EmailProviderPanel({ venueId, onFlash, api = adminApi }) {
  const [loading, setLoading]   = useState(true);
  const [form, setForm]         = useState(null); // masked snapshot + drafts
  const [saving, setSaving]     = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);

  useEffect(() => { if (venueId) load(); }, [venueId]); // eslint-disable-line

  async function load() {
    setLoading(true);
    try {
      const cfg = await api.getEmailProvider(venueId);
      setForm({
        provider:          cfg.provider || "sendgrid",
        from_email:        cfg.from_email || "",
        from_name:         cfg.from_name || "",
        sendgrid_api_key:  "",          // blank = keep stored secret
        sendgrid_api_key_masked: cfg.sendgrid_api_key_masked || "",
        has_sendgrid_key:  !!cfg.has_sendgrid_key,

        smtp_host:         cfg.smtp_host || "",
        smtp_port:         cfg.smtp_port || 587,
        smtp_secure:       !!cfg.smtp_secure,
        smtp_username:     cfg.smtp_username || "",
        smtp_password:     "",
        has_smtp_password: !!cfg.has_smtp_password,

        ses_region:        cfg.ses_region || "us-east-1",
        ses_smtp_username: cfg.ses_smtp_username || "",
        ses_smtp_password: "",
        has_ses_password:  !!cfg.has_ses_password,
      });
    } catch (e) {
      onFlash?.(e.message, "error");
    }
    setLoading(false);
  }

  function update(k, v) { setForm((p) => ({ ...p, [k]: v })); setVerifyResult(null); }

  function buildPatch() {
    if (!form) return {};
    const p = {
      provider:   form.provider,
      from_email: form.from_email,
      from_name:  form.from_name,
    };
    if (form.provider === "sendgrid") {
      if (form.sendgrid_api_key) p.sendgrid_api_key = form.sendgrid_api_key;
    } else if (form.provider === "smtp") {
      p.smtp_host = form.smtp_host;
      p.smtp_port = Number(form.smtp_port) || 587;
      p.smtp_secure = !!form.smtp_secure;
      p.smtp_username = form.smtp_username;
      if (form.smtp_password) p.smtp_password = form.smtp_password;
    } else if (form.provider === "ses") {
      p.ses_region = form.ses_region;
      p.ses_smtp_username = form.ses_smtp_username;
      if (form.ses_smtp_password) p.ses_smtp_password = form.ses_smtp_password;
    }
    return p;
  }

  async function save() {
    setSaving(true);
    try {
      await api.saveEmailProvider(venueId, buildPatch());
      onFlash?.("Email provider saved");
      await load();
    } catch (e) {
      onFlash?.(e.message, "error");
    }
    setSaving(false);
  }

  async function verify() {
    setVerifying(true);
    setVerifyResult(null);
    try {
      const r = await api.verifyEmailProvider(venueId, buildPatch());
      setVerifyResult({ ok: r.ok !== false, message: r.ok ? `Credentials valid (${r.provider})` : (r.error || "Failed") });
    } catch (e) {
      setVerifyResult({ ok: false, message: e.message || "Verification failed" });
    }
    setVerifying(false);
  }

  if (loading || !form) return <div className="loading-state"><div className="spinner" /></div>;

  const current = PROVIDERS.find((p) => p.id === form.provider) || PROVIDERS[0];

  return (
    <div className="settings-card">
      <div className="settings-card-header">
        <h3>Email Provider</h3>
        <p>
          Outbound email backend for this venue. Applies to welcome,
          renewal, and test emails fired from the member signup flow.
        </p>
      </div>

      <div className="epp-provider-grid">
        {PROVIDERS.map((p) => (
          <button
            key={p.id}
            type="button"
            className={`epp-provider-card ${form.provider === p.id ? "active" : ""}`}
            onClick={() => update("provider", p.id)}
          >
            <span className="epp-provider-label">{p.label}</span>
            <span className="epp-provider-blurb">{p.blurb}</span>
            {form.provider === p.id && <span className="epp-provider-check">✓</span>}
          </button>
        ))}
      </div>

      <div className="epp-section">
        <div className="epp-section-title">Sender identity</div>
        <div className="epp-row">
          <label className="form-field">
            <span>From email</span>
            <input
              type="email"
              value={form.from_email}
              onChange={(e) => update("from_email", e.target.value)}
              placeholder="noreply@yourdomain.com"
            />
          </label>
          <label className="form-field">
            <span>From name</span>
            <input
              type="text"
              value={form.from_name}
              onChange={(e) => update("from_name", e.target.value)}
              placeholder="Circle Solution"
            />
          </label>
        </div>
        <p className="epp-hint">
          {form.provider === "sendgrid"
            ? "Must be a verified single sender or live on an authenticated domain in SendGrid."
            : form.provider === "ses"
            ? "Must be a verified identity in the same AWS region as the SES credentials below."
            : "Should match an address your SMTP server is allowed to send as."}
        </p>
      </div>

      {form.provider === "sendgrid" && (
        <div className="epp-section">
          <div className="epp-section-title">SendGrid credentials</div>
          <label className="form-field">
            <span>API key</span>
            <PasswordInput
              value={form.sendgrid_api_key}
              onChange={(e) => update("sendgrid_api_key", e.target.value)}
              placeholder={form.has_sendgrid_key ? `${form.sendgrid_api_key_masked} — leave blank to keep` : "SG.xxxx…"}
              autoComplete="new-password"
            />
          </label>
          <p className="epp-hint">SendGrid → Settings → API Keys → create one with <code>Mail Send</code> permission.</p>
        </div>
      )}

      {form.provider === "smtp" && (
        <div className="epp-section">
          <div className="epp-section-title">SMTP server</div>
          <div className="epp-row">
            <label className="form-field">
              <span>Host</span>
              <input type="text" value={form.smtp_host} onChange={(e) => update("smtp_host", e.target.value)} placeholder="smtp.mailgun.org" />
            </label>
            <label className="form-field" style={{ maxWidth: 140 }}>
              <span>Port</span>
              <input type="number" value={form.smtp_port} onChange={(e) => update("smtp_port", e.target.value)} placeholder="587" />
            </label>
          </div>
          <div className="epp-row">
            <label className="form-field">
              <span>Username</span>
              <input type="text" value={form.smtp_username} onChange={(e) => update("smtp_username", e.target.value)} autoComplete="username" />
            </label>
            <label className="form-field">
              <span>Password</span>
              <PasswordInput
                value={form.smtp_password}
                onChange={(e) => update("smtp_password", e.target.value)}
                placeholder={form.has_smtp_password ? "•••••••• — leave blank to keep" : ""}
                autoComplete="new-password"
              />
            </label>
          </div>
          <label className="epp-checkbox">
            <input
              type="checkbox"
              checked={!!form.smtp_secure}
              onChange={(e) => update("smtp_secure", e.target.checked)}
            />
            <span>Use implicit TLS (port 465). Leave unchecked for STARTTLS on 587.</span>
          </label>
        </div>
      )}

      {form.provider === "ses" && (
        <div className="epp-section">
          <div className="epp-section-title">Amazon SES (via SMTP)</div>
          <div className="epp-row">
            <div className="form-field">
              <span>Region</span>
              <AdminSelect
                value={form.ses_region}
                onChange={(v) => update("ses_region", v)}
                options={SES_REGIONS.map((r) => ({ value: r, label: r }))}
              />
            </div>
          </div>
          <div className="epp-row">
            <label className="form-field">
              <span>SMTP username</span>
              <input type="text" value={form.ses_smtp_username} onChange={(e) => update("ses_smtp_username", e.target.value)} autoComplete="username" />
            </label>
            <label className="form-field">
              <span>SMTP password</span>
              <PasswordInput
                value={form.ses_smtp_password}
                onChange={(e) => update("ses_smtp_password", e.target.value)}
                placeholder={form.has_ses_password ? "•••••••• — leave blank to keep" : ""}
                autoComplete="new-password"
              />
            </label>
          </div>
          <p className="epp-hint">
            Create SMTP credentials in the SES console → SMTP settings → Create SMTP credentials.
            These are <strong>different</strong> from your IAM access key/secret.
          </p>
        </div>
      )}

      {verifyResult && (
        <div className={`su-alert ${verifyResult.ok ? "success" : "error"}`} style={{ margin: "0.75rem 0 0" }}>
          {verifyResult.ok ? "✅" : "⚠"} {verifyResult.message}
        </div>
      )}

      <div className="epp-actions">
        <button type="button" className="btn btn-outline" onClick={verify} disabled={verifying}>
          {verifying ? "Verifying…" : "Test connection"}
        </button>
        <button type="button" className="btn btn-primary" onClick={save} disabled={saving}>
          {saving ? "Saving…" : "Save provider"}
        </button>
      </div>

      <p className="epp-hint" style={{ marginTop: "1rem" }}>
        Active provider: <strong>{current.label}</strong>
        {form.provider === "sendgrid" && !form.has_sendgrid_key && !form.sendgrid_api_key && (
          <span> — no API key set yet. Falls back to the <code>SENDGRID_API_KEY</code> env var.</span>
        )}
      </p>
    </div>
  );
}
